import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Image, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";
import { Textarea } from "@/components/ui/textarea";

export default function Feed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [showPostForm, setShowPostForm] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/posts`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);

    try {
      const response = await fetch(`${API}/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newPost.trim(), images: [] }),
      });

      if (response.ok) {
        const post = await response.json();
        setPosts([post, ...posts]);
        setNewPost("");
        setShowPostForm(false);
        toast.success("Post created!");
      }
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const response = await fetch(`${API}/posts/${postId}/like`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const { liked } = await response.json();
        setPosts(
          posts.map((p) =>
            p.post_id === postId
              ? {
                  ...p,
                  likes: liked
                    ? [...(p.likes || []), user.user_id]
                    : (p.likes || []).filter((id) => id !== user.user_id),
                }
              : p
          )
        );
      }
    } catch (error) {
      // Like state refreshes on next fetch
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Create Post Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowPostForm(true)}
          className="w-full brutal-card mb-8 text-left flex items-center gap-4"
          data-testid="create-post-btn"
        >
          <div className="w-12 h-12 border-2 border-black bg-gray-200 flex items-center justify-center overflow-hidden">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-black">{user?.name?.[0]}</span>
            )}
          </div>
          <span className="text-[#4A4A4A]">What's on your mind?</span>
        </motion.button>

        {/* Posts List */}
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="brutal-card animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 border-2 border-black"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 w-24"></div>
                  </div>
                </div>
                <div className="h-20 bg-gray-200"></div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="brutal-card text-center py-16" data-testid="no-posts">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-[#FF66B2]" />
            <h2 className="text-2xl font-bold mb-2">No posts yet</h2>
            <p className="text-[#4A4A4A]">Be the first to share something!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post, index) => (
              <motion.div
                key={post.post_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="post-card"
                data-testid={`post-${post.post_id}`}
              >
                {/* Post Header */}
                <div className="post-header">
                  <div
                    className="w-12 h-12 border-2 border-black bg-gray-200 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/profile/${post.author_id}`)}
                  >
                    {post.author_picture ? (
                      <img
                        src={post.author_picture}
                        alt={post.author_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-black">
                        {post.author_name?.[0]}
                      </div>
                    )}
                  </div>
                  <div>
                    <p
                      className="font-bold cursor-pointer hover:underline"
                      onClick={() => navigate(`/profile/${post.author_id}`)}
                    >
                      {post.author_name}
                    </p>
                    <p className="text-sm text-[#4A4A4A]">
                      {new Date(post.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>

                {/* Post Content */}
                <p className="post-content">{post.content}</p>

                {/* Post Actions */}
                <div className="post-actions">
                  <button
                    onClick={() => likePost(post.post_id)}
                    className={`flex items-center gap-2 font-bold transition-colors ${
                      post.likes?.includes(user?.user_id)
                        ? "text-[#FF4D4D]"
                        : "text-[#4A4A4A] hover:text-[#FF4D4D]"
                    }`}
                    data-testid={`like-btn-${post.post_id}`}
                  >
                    <Heart
                      className="w-5 h-5"
                      fill={post.likes?.includes(user?.user_id) ? "currentColor" : "none"}
                    />
                    {post.likes?.length || 0}
                  </button>
                  <button
                    className="flex items-center gap-2 font-bold text-[#4A4A4A] hover:text-[#FF66B2] transition-colors"
                    data-testid={`comment-btn-${post.post_id}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    {post.comments?.length || 0}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Create Post Modal */}
      <AnimatePresence>
        {showPostForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPostForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="brutal-card w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
              data-testid="create-post-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Create Post</h2>
                <button
                  onClick={() => setShowPostForm(false)}
                  className="brutal-btn bg-white p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <Textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="What's on your mind?"
                className="brutal-input min-h-[150px] mb-4"
                data-testid="post-content-input"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowPostForm(false)}
                  className="brutal-btn bg-white"
                >
                  Cancel
                </button>
                <button
                  onClick={createPost}
                  disabled={!newPost.trim() || posting}
                  className="brutal-btn-primary disabled:opacity-50"
                  data-testid="submit-post-btn"
                >
                  {posting ? "Posting..." : "Post"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
